import { useRef, useEffect } from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import { styled } from '@mui/system';

const MessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isAgent',
})<{ isAgent: boolean }>(({ theme, isAgent }) => ({
  display: 'inline-block',
  maxWidth: '80%',
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1.5),
  backgroundColor: isAgent ? '#f8e5e5' : '#e3f2fd',
  color: theme.palette.text.primary,
  ...(isAgent
    ? { borderTopLeftRadius: 0 }
    : { borderTopRightRadius: 0, marginLeft: 'auto' }),
}));

const TimeStamp = styled(Typography)({
  fontSize: '0.7rem',
  color: 'rgba(0, 0, 0, 0.54)',
  marginTop: 2,
});

const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAgent',
})<{ isAgent: boolean }>(({ isAgent }) => ({
  marginBottom: 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: isAgent ? 'flex-start' : 'flex-end',
}));

interface Transcript {
  source: string;
  message: string;
  timestamp: Date;
}

interface ConversationAreaProps {
  transcript: Transcript[];
  isSpeaking: boolean;
}

export const ConversationArea = ({
  transcript,
  isSpeaking: _isSpeaking,
}: ConversationAreaProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcript]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ textAlign: 'center', py: 1 }}>
        <Typography variant="body2" color="error" fontWeight={500}>
          Prudential Singapore
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Insurance
        </Typography>
      </Box>

      <Divider />

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
        }}
      >
        {transcript.map((item, index) => {
          // Handle system messages differently
          if (item.source === 'system') {
            return (
              <Box key={index} sx={{ textAlign: 'center', my: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {item.message}
                </Typography>
              </Box>
            );
          }

          return (
            <MessageContainer key={index} isAgent={item.source === 'ai'}>
              <MessageBubble isAgent={item.source === 'ai'} elevation={0}>
                {item.message}
              </MessageBubble>
              <TimeStamp>
                {item.timestamp.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </TimeStamp>
            </MessageContainer>
          );
        })}

        <div ref={messagesEndRef} />
      </Box>

      <Divider />

      {/* Removed bottom status indicator for streamlined scripted calls */}
    </Box>
  );
};
